import React from 'react';
import { useIntl } from 'react-intl';

import { Box } from '~/eds/src/components/Box/Box';
import { Chip } from '~/eds/src/components/Chip/Chip';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { LoadingShimmer } from '~/eds/src/components/LoadingShimmer/LoadingShimmer';
import { Text } from '~/eds/src/components/Text/Text';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import { PlaybookWithId } from '~/redux/api/types/playbooks/playbooks';

import { Delete } from '../delete/Delete';
import { GuidelineReadOnly } from './GuidelineReadOnly';

type Props = {
  id: string;
  hasEditPermission?: boolean;
  onEdit: (playbook: PlaybookWithId | undefined) => void;
};

export const Details = ({ id, hasEditPermission = false, onEdit }: Props) => {
  const intl = useIntl();
  const isDraftPublishEnabled = useFlag(FlagType.PlaybookDraftPublish);
  const {
    data: playbook,
    isFetching: isFetchingPlaybook,
    isError,
  } = api.endpoints.getPlaybook.useQuery({ playbookId: id });

  const { isFetching: isFetchingWorkflows, data: workflowsData } =
    api.endpoints.getAvailableWorkflows.useQuery('');

  const workflowMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    if (workflowsData) {
      workflowsData.forEach((workflow: any) => {
        map[workflow.id] = workflow.name;
      });
    }
    return map;
  }, [workflowsData]);

  return (
    <Layout flex="1" direction="column" w="100%" overflowY="scroll">
      {isError ? (
        <Text variant="body-medium">
          {intl.formatMessage({
            id: 'playbooks.details.error',
            defaultMessage: 'Error loading playbook details.',
            description: 'Error message when playbook details fail to load',
          })}
        </Text>
      ) : (
        <>
          {isFetchingPlaybook ? (
            <Layout preset="subsections" flex="1">
              <LoadingShimmer height="header.height" />
              <LoadingShimmer height="header.height" />
              <LoadingShimmer height="120px" />
              <LoadingShimmer height="340px" />
            </Layout>
          ) : (
            <>
              <Layout
                borderBottom="border.divider"
                minH="header.height"
                justify="space-between"
                px={4}
                align="center"
                bg="white"
                flex="none"
              >
                <Layout align="center" spacing={2} flex="1" minWidth={0}>
                  {/* eslint-disable-next-line jsx-a11y/aria-role -- TODO: EKP-42656 */}
                  <Text variant="body-bold" fontSize="16" role="subheading">
                    {playbook?.data?.attributes?.name}
                  </Text>
                  {isDraftPublishEnabled &&
                    playbook?.data?.attributes?.status && (
                      <Chip
                        text={
                          playbook.data.attributes.status === 'DRAFT'
                            ? intl.formatMessage({
                                id: 'playbooks.status.draft',
                                defaultMessage: 'Draft',
                                description: 'Playbook status label for draft',
                              })
                            : intl.formatMessage({
                                id: 'playbooks.status.published',
                                defaultMessage: 'Published',
                                description:
                                  'Playbook status label for published',
                              })
                        }
                        status={
                          playbook.data.attributes.status === 'PUBLISHED'
                            ? 'success'
                            : 'warning'
                        }
                      />
                    )}
                </Layout>

                <Layout preset="buttons">
                  <IconButton
                    icon="edit"
                    onClick={() => onEdit(playbook?.data?.attributes)}
                    disabled={!hasEditPermission}
                  />
                  <Delete
                    playbookId={id}
                    playbookName={playbook?.data?.attributes?.name ?? ''}
                    disabled={!hasEditPermission}
                  />
                </Layout>
              </Layout>
              <Layout
                direction="column"
                p={4}
                spacing={4}
                maxW="100%"
                overflowY="scroll"
              >
                {playbook?.data?.attributes?.description && (
                  <Layout direction="column">
                    <Text variant="tiny">
                      {intl.formatMessage({
                        id: 'playbooks.details.description',
                        defaultMessage: 'Description',
                        description: 'Label for playbook description section',
                      })}
                    </Text>
                    <Box bg="background.info" p={3} borderRadius={4}>
                      <Text variant="body-medium">
                        {playbook?.data?.attributes?.description}
                      </Text>
                    </Box>
                  </Layout>
                )}
                <Layout direction="column">
                  <Text variant="tiny">
                    {intl.formatMessage({
                      id: 'playbooks.details.scope',
                      defaultMessage: 'Scope',
                      description: 'Label for playbook scope section',
                    })}
                  </Text>
                  <Box bg="background.info" p={3} borderRadius={4}>
                    <Layout borderBottom="border.divider" pb={2}>
                      <Text variant="body-medium" color="text.quiet" flex="1">
                        {intl.formatMessage({
                          id: 'playbooks.details.workflow',
                          defaultMessage: 'Workflow',
                          description:
                            'Label for workflow IDs in playbook scope',
                        })}
                      </Text>

                      {isFetchingWorkflows ? (
                        <LoadingShimmer height="20px" width="100px" />
                      ) : (
                        <Layout direction="column" flex="3" spacing={2}>
                          <Text variant="body-medium">
                            {(
                              playbook?.data?.attributes?.restrictions
                                ?.workflow_ids ?? []
                            )
                              .map(
                                (workflowId) =>
                                  workflowMap[workflowId] || workflowId,
                              )
                              .join(', ')}
                          </Text>
                        </Layout>
                      )}
                    </Layout>
                    {playbook?.data?.attributes?.restrictions?.contract_types
                      ?.length ? (
                      <Layout py={2}>
                        <Text variant="body-medium" color="text.quiet" flex="1">
                          {intl.formatMessage({
                            id: 'playbooks.details.contractType',
                            defaultMessage: 'Contract Type',
                            description:
                              'Label for contract types in playbook scope',
                          })}
                        </Text>
                        <Layout direction="column" flex="3" spacing={2}>
                          {(
                            playbook?.data?.attributes?.restrictions
                              ?.contract_types ?? []
                          ).map((contractType) => (
                            <Text key={contractType} variant="body-medium">
                              {contractType}
                            </Text>
                          ))}
                        </Layout>
                      </Layout>
                    ) : null}
                  </Box>
                </Layout>
                <Layout direction="column" spacing={2} maxW="100%" minWidth={0}>
                  {(playbook?.data?.attributes?.guidelines ?? []).map(
                    (guideline) => (
                      <GuidelineReadOnly
                        key={`guideline-${guideline.uuid}`}
                        name={guideline.name ?? ''}
                        guideline={guideline.guideline}
                        sample={guideline.sample}
                        suggestRedlines={!!guideline.suggest_redlines}
                        variations={guideline.variations}
                        mode="boxed"
                      />
                    ),
                  )}
                </Layout>
              </Layout>
            </>
          )}
        </>
      )}
    </Layout>
  );
};
